import { useEffect, useRef, useState, useMemo } from 'react';
import type { TransformerModel, StemTarget } from '../../lib/model-registry';

// ═══════════════════════════════════════════════════════════════════════════════
// Frequency / Mel-Scale Constants
// ═══════════════════════════════════════════════════════════════════════════════

const MEL_BANDS = 64;
const FREQ_MIN = 20;
const FREQ_MAX = 20000;

function hzToMel(hz: number): number {
  return 2595 * Math.log10(1 + hz / 700);
}

function melToHz(mel: number): number {
  return 700 * (Math.pow(10, mel / 2595) - 1);
}

const MEL_MIN = hzToMel(FREQ_MIN);
const MEL_MAX = hzToMel(FREQ_MAX);

// Stem frequency ranges (approximate center of energy)
const STEM_FREQ_RANGES: Record<StemTarget, { low: number; high: number; peak: number; color: string }> = {
  vocals:       { low: 85,    high: 8000,  peak: 2500, color: '#00FFFF' },
  drums:        { low: 30,    high: 12000, peak: 200,  color: '#F97316' },
  bass:         { low: 30,    high: 500,   peak: 100,  color: '#A78BFA' },
  guitar:       { low: 80,    high: 6000,  peak: 1200, color: '#FBBF24' },
  piano:        { low: 28,    high: 4200,  peak: 800,  color: '#34D399' },
  other:        { low: 100,   high: 16000, peak: 4000, color: '#94A3B8' },
  instrumental: { low: 28,    high: 16000, peak: 1000, color: '#EC4899' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Spectrum data generation (synthetic but realistic)
// ═══════════════════════════════════════════════════════════════════════════════

function generateStemSpectrum(stem: StemTarget, bands: number = MEL_BANDS): number[] {
  const range = STEM_FREQ_RANGES[stem];
  const data: number[] = [];

  for (let i = 0; i < bands; i++) {
    const normPos = i / bands;
    const hz = melToHz(MEL_MIN + normPos * (MEL_MAX - MEL_MIN));

    if (hz < range.low || hz > range.high) {
      data.push(Math.random() * 0.05);
      continue;
    }

    // Bell curve energy around peak
    const logHz = Math.log10(hz);
    const logPeak = Math.log10(range.peak);
    const spread = 0.8;
    const dist = (logHz - logPeak) / spread;
    const energy = Math.exp(-0.5 * dist * dist);

    // Add natural variation
    const noise = 0.85 + Math.random() * 0.3;
    data.push(Math.min(1, energy * noise));
  }
  return data;
}

function generateModelCoverage(model: TransformerModel, bands: number = MEL_BANDS): number[] {
  const combined = new Array(bands).fill(0);
  for (const stem of model.stems) {
    const stemData = generateStemSpectrum(stem, bands);
    for (let i = 0; i < bands; i++) {
      combined[i] = Math.max(combined[i], stemData[i]);
    }
  }
  return combined;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Model Spectrum Visualization (for ModelSelector cards)
// ═══════════════════════════════════════════════════════════════════════════════

interface ModelSpectrumProps {
  model: TransformerModel;
  height?: number;
  animated?: boolean;
}

export function ModelSpectrum({ model, height = 48, animated = true }: ModelSpectrumProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrame = useRef<number>(0);
  const phaseRef = useRef(0);

  const baseData = useMemo(() => generateModelCoverage(model), [model]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      phaseRef.current += 0.02;

      const barW = w / MEL_BANDS;
      const gap = 1;

      for (let i = 0; i < MEL_BANDS; i++) {
        const base = baseData[i];
        const animMod = animated ? 0.85 + 0.15 * Math.sin(phaseRef.current + i * 0.15) : 1;
        const val = base * animMod;

        const barH = val * (h - 4);
        const x = i * barW;
        const y = h - barH;

        // Gradient color based on frequency
        const normFreq = i / MEL_BANDS;
        const r = Math.round(0 + normFreq * 80);
        const g = Math.round(200 + normFreq * 55);
        const b = Math.round(255 - normFreq * 60);
        const alpha = 0.3 + val * 0.7;

        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx.fillRect(x + gap / 2, y, barW - gap, barH);

        // Peak glow
        if (val > 0.7) {
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${val * 0.4})`;
          ctx.fillRect(x + gap / 2, y - 2, barW - gap, 2);
        }
      }

      // Frequency axis labels
      ctx.font = '8px monospace';
      ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
      const labels = [
        { hz: 100, label: '100' },
        { hz: 1000, label: '1k' },
        { hz: 5000, label: '5k' },
        { hz: 15000, label: '15k' },
      ];
      for (const l of labels) {
        const mel = hzToMel(l.hz);
        const xPos = ((mel - MEL_MIN) / (MEL_MAX - MEL_MIN)) * w;
        ctx.fillText(l.label, xPos - 6, h - 1);
      }

      if (animated) {
        animFrame.current = requestAnimationFrame(draw);
      }
    }

    draw();
    return () => cancelAnimationFrame(animFrame.current);
  }, [baseData, animated, height]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full rounded"
      style={{ height: `${height}px` }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Stem Separation Spectrum (multi-stem frequency breakdown)
// ═══════════════════════════════════════════════════════════════════════════════

interface StemSpectrumProps {
  stems: StemTarget[];
  height?: number;
  showLabels?: boolean;
  compact?: boolean;
}

export function StemSpectrum({ stems, height = 80, showLabels = true, compact = false }: StemSpectrumProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrame = useRef<number>(0);
  const phaseRef = useRef(0);

  const stemKey = stems.join(',');
  const stemData = useMemo(() => {
    const list = (stemKey ? stemKey.split(',') : []) as StemTarget[];
    return list.map((stem) => ({
      stem,
      data: generateStemSpectrum(stem),
      color: STEM_FREQ_RANGES[stem].color,
    }));
  }, [stemKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const labelH = showLabels ? 14 : 0;
    const drawH = h - labelH;

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      phaseRef.current += 0.015;

      const barW = w / MEL_BANDS;

      // Draw each stem as overlapping frequency curves
      for (const { data, color } of stemData) {
        ctx.beginPath();
        ctx.moveTo(0, drawH);

        for (let i = 0; i < MEL_BANDS; i++) {
          const val = data[i] * (0.85 + 0.15 * Math.sin(phaseRef.current + i * 0.12));
          const x = i * barW + barW / 2;
          const y = drawH - val * (drawH - 4);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }

        ctx.lineTo(w, drawH);
        ctx.closePath();

        // Fill with semi-transparent color
        const [r, g, b] = hexToRgb(color);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.15)`;
        ctx.fill();

        // Stroke the line
        ctx.beginPath();
        for (let i = 0; i < MEL_BANDS; i++) {
          const val = data[i] * (0.85 + 0.15 * Math.sin(phaseRef.current + i * 0.12));
          const x = i * barW + barW / 2;
          const y = drawH - val * (drawH - 4);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.8)`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Frequency axis
      if (showLabels) {
        ctx.font = '8px monospace';
        ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
        const markers = [
          { hz: 50, label: '50' },
          { hz: 200, label: '200' },
          { hz: 1000, label: '1k' },
          { hz: 4000, label: '4k' },
          { hz: 10000, label: '10k' },
        ];
        for (const m of markers) {
          const mel = hzToMel(m.hz);
          const xPos = ((mel - MEL_MIN) / (MEL_MAX - MEL_MIN)) * w;
          ctx.fillText(m.label, xPos - 6, h - 2);
          // Tick mark
          ctx.fillStyle = 'rgba(148, 163, 184, 0.2)';
          ctx.fillRect(xPos, 0, 0.5, drawH);
          ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
        }
      }

      animFrame.current = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(animFrame.current);
  }, [stemData, height, showLabels]);

  return (
    <div className={compact ? '' : 'space-y-2'}>
      <canvas
        ref={canvasRef}
        className="w-full rounded bg-black/30"
        style={{ height: `${height}px` }}
      />
      {showLabels && !compact && (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {stemData.map(({ stem, color }) => (
            <div key={stem} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: color }} />
              <span className="text-[9px] font-mono text-slate-300">{stem}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Real-time Analyzer (for active pipeline monitoring)
// ═══════════════════════════════════════════════════════════════════════════════

interface LiveAnalyzerProps {
  stems: StemTarget[];
  phase: string;
  progress: number;
  modelName: string;
  height?: number;
}

export function LiveAnalyzer({ stems, phase, progress, modelName, height = 100 }: LiveAnalyzerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrame = useRef<number>(0);
  const phaseRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setActiveIndex(i => (i + 1) % stems.length), 2000);
    return () => clearInterval(interval);
  }, [stems.length]);

  const currentStem = stems[activeIndex];
  const stemColor = STEM_FREQ_RANGES[currentStem]?.color ?? '#00FFFF';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      phaseRef.current += 0.03;

      // Waterfall-style spectrum
      const bands = 48;
      const barW = w / bands;
      const data = generateStemSpectrum(currentStem, bands);

      for (let i = 0; i < bands; i++) {
        const baseVal = data[i];
        const animVal = baseVal * (0.6 + 0.4 * Math.sin(phaseRef.current * 1.5 + i * 0.2));
        const val = phase === 'separate' ? animVal * (progress / 100) : animVal;

        const barH = val * (h - 8);
        const x = i * barW;
        const y = h - barH;

        // Gradient bar
        const grad = ctx.createLinearGradient(x, y, x, h);
        const [r, g, b] = hexToRgb(stemColor);
        grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.9 * val})`);
        grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0.1)`);
        ctx.fillStyle = grad;
        ctx.fillRect(x + 0.5, y, barW - 1, barH);

        // Mirror reflection
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.1 * val})`;
        ctx.fillRect(x + 0.5, h, barW - 1, -Math.min(barH * 0.2, 6));
      }

      // Progress line
      if (progress > 0 && progress < 100) {
        const px = (progress / 100) * w;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, h);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      animFrame.current = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(animFrame.current);
  }, [currentStem, phase, progress, height, stemColor]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: stemColor }} />
          <span className="text-[10px] font-mono text-slate-200">{currentStem}</span>
        </div>
        <span className="text-[9px] font-mono text-slate-400">{modelName} | {phase} | {progress}%</span>
      </div>
      <canvas
        ref={canvasRef}
        className="w-full rounded border border-white/[0.06] bg-black/40"
        style={{ height: `${height}px` }}
      />
      <div className="flex gap-1">
        {stems.map((s, i) => (
          <button
            key={s}
            onClick={() => setActiveIndex(i)}
            className={`flex-1 py-1 text-[8px] font-mono uppercase tracking-wider rounded-sm transition-all ${
              i === activeIndex
                ? 'text-white border border-white/20'
                : 'text-slate-500 border border-transparent hover:text-slate-300'
            }`}
            style={i === activeIndex ? { backgroundColor: `${STEM_FREQ_RANGES[s].color}20`, borderColor: `${STEM_FREQ_RANGES[s].color}40` } : undefined}
          >
            {s.slice(0, 3)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Waveform Comparison (before / after separation view)
// ═══════════════════════════════════════════════════════════════════════════════

interface WaveformCompareProps {
  stems: StemTarget[];
  height?: number;
}

export function WaveformCompare({ stems, height = 32 }: WaveformCompareProps) {
  return (
    <div className="space-y-1">
      {stems.map((stem) => (
        <WaveformRow key={stem} stem={stem} height={height} />
      ))}
    </div>
  );
}

function WaveformRow({ stem, height }: { stem: StemTarget; height: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const phaseRef = useRef(Math.random() * 100);

  const color = STEM_FREQ_RANGES[stem]?.color ?? '#94A3B8';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const midY = h / 2;
    const samples = Math.floor(w / 2);

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      phaseRef.current += 0.008;

      const [r, g, b] = hexToRgb(color);

      ctx.beginPath();
      for (let i = 0; i < samples; i++) {
        const x = (i / samples) * w;
        const freq1 = 0.05 + Math.random() * 0.01;
        const freq2 = 0.12;
        const amp = 0.3 + 0.7 * Math.sin(phaseRef.current + i * 0.02) * Math.sin(i * freq1);
        const val = amp * Math.sin(i * freq2 + phaseRef.current * 2);
        const y = midY + val * (midY - 2);

        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.7)`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Fill under curve
      ctx.lineTo(w, midY);
      ctx.lineTo(0, midY);
      ctx.closePath();
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.08)`;
      ctx.fill();

      animRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [color, height]);

  return (
    <div className="flex items-center gap-2">
      <span className="text-[8px] font-mono text-slate-400 w-[50px] truncate uppercase tracking-wider">{stem}</span>
      <canvas
        ref={canvasRef}
        className="flex-1 rounded-sm"
        style={{ height: `${height}px` }}
      />
      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Frequency Band Heatmap (compact model comparison)
// ═══════════════════════════════════════════════════════════════════════════════

interface BandHeatmapProps {
  models: TransformerModel[];
  height?: number;
}

export function BandHeatmap({ models, height = 60 }: BandHeatmapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const rowH = h / models.length;

    models.forEach((model, rowIdx) => {
      const coverage = generateModelCoverage(model, 32);
      const cellW = w / 32;

      for (let i = 0; i < 32; i++) {
        const val = coverage[i];
        const x = i * cellW;
        const y = rowIdx * rowH;

        ctx.fillStyle = `rgba(0, ${Math.round(200 + val * 55)}, ${Math.round(255 - val * 60)}, ${0.1 + val * 0.8})`;
        ctx.fillRect(x + 0.5, y + 0.5, cellW - 1, rowH - 1);
      }
    });
  }, [models]);

  return (
    <div className="space-y-1">
      <canvas
        ref={canvasRef}
        className="w-full rounded border border-white/[0.06]"
        style={{ height: `${height}px` }}
      />
      <div className="flex justify-between text-[8px] font-mono text-slate-500">
        <span>20 Hz</span>
        <span>200</span>
        <span>2k</span>
        <span>20k Hz</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Utilities
// ═══════════════════════════════════════════════════════════════════════════════

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}
